import React, { useEffect, useMemo, useState } from "react";
import { Spinner, Typography } from "@material-tailwind/react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Table } from "../../../../../components/table/Table";
import { api } from "../../../../../services/api/api";
import { WorkorderStatus } from "../../../../../types/workorder/workorderStatus";

interface HistoryTabProps {
  workorder: EasyTech.Workorder | null;
  refreshKey?: number;
}

interface WorkorderActionUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * One timeline row, as the API now returns it.
 *
 * The endpoint used to hand back raw SQL rows, so this component read every field through three
 * spellings. It returns a real DTO now, and the shim is gone: if a field stops arriving, that should
 * surface as a visible break rather than be absorbed silently.
 */
interface WorkorderAction {
  id: string;
  created?: string | number;
  actionType?: string;
  user?: WorkorderActionUser;
  userId?: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  /** Resolved name behind oldValue/newValue where one exists — server-side, for every row. */
  oldValueLabel?: string;
  newValueLabel?: string;
  description?: string;
  /** `ACTION` for a user action, `EXTERNAL` for an Odoo sync event. */
  source?: string;
}

interface WorkorderActionsPage {
  data?: WorkorderAction[];
  totalPages?: number;
}

interface HistoryRow {
  id: string;
  created?: string | number;
  actionLabel: string;
  user?: WorkorderActionUser;
  userId?: string;
  change?: string;
}

const PAGE_SIZE = 100;

/** Fields whose values are a work-order status, and so translate through `workorder:status.*`. */
const STATUS_FIELD = "status";
/**
 * Fields whose value is an entity id rather than something readable.
 *
 * `technician` arrives with a resolved name; the rest do not, and printing a bare UUID would
 * recreate for three fields the exact problem this work fixed for one. Where no name is available
 * the row still says the field changed — it just does not claim to say what to.
 */
const ENTITY_FIELDS = ["technician", "customer", "type", "origin"];
/** Fields holding a date, which must not reach the screen as a raw Date.toString(). */
const DATE_FIELDS = [
  "schedule.start", "schedule.end",
  "time.start", "time.end",
  "travelTime.start", "travelTime.end",
];
/** Fields holding a boolean. */
const BOOLEAN_FIELDS = ["onCallDuty"];
/**
 * Actions whose stored description is written for a log, not for a planner. `REPORT_EMAIL_FAILED`
 * is built as `to=<recipient> reason=<exception message>` — a customer address and an internal
 * stack message, neither of which belongs on this screen.
 */
const OPAQUE_DESCRIPTION_ACTIONS = ["REPORT_EMAIL_FAILED"];

const isWorkorderStatus = (status?: string): status is WorkorderStatus => {
  return !!status && Object.values(WorkorderStatus).includes(status as WorkorderStatus);
};

const formatStatus = (t: TFunction, status?: string) => {
  if (!status) return "-";
  return isWorkorderStatus(status)
    ? t(`workorder:status.${status.toLowerCase()}`, { defaultValue: status }).toUpperCase()
    : status.toUpperCase();
};

const formatDateTime = (value?: string | number) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return `${date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })} - ${date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/** Field keys flatten the dots, because i18next would otherwise read them as nesting. */
const formatFieldName = (t: TFunction, fieldName?: string) => {
  if (!fieldName) return undefined;
  return t(`workorder:actionField.${fieldName.replace(/\./g, "_")}`, { defaultValue: fieldName });
};

const formatActionType = (t: TFunction, actionType?: string) => {
  if (!actionType) return "-";
  return t(`workorder:actionType.${actionType.toLowerCase()}`, { defaultValue: actionType });
};

/**
 * Render one side of a change, by the shape of the value rather than by action type.
 *
 * Three rules cover all 43 types. Without them the majority of rows — everything `logFieldChange`
 * writes — would show `String.valueOf(...)` of whatever Java held: `Thu Aug 14 09:12:00 CEST 2026`,
 * a bare `true`, or a UUID.
 */
const formatValue = (
  t: TFunction,
  fieldName: string | undefined,
  raw: string | undefined,
  resolvedLabel: string | undefined
): string | undefined => {
  if (resolvedLabel) return resolvedLabel;
  if (raw === undefined || raw === null || raw === "" || raw === "null") {
    return t("workorder:actionValue.notSet", { defaultValue: "Not set" });
  }

  if (fieldName === STATUS_FIELD) return formatStatus(t, raw);

  if (fieldName && DATE_FIELDS.includes(fieldName)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : formatDateTime(raw);
  }

  if (fieldName && BOOLEAN_FIELDS.includes(fieldName)) {
    if (raw === "true") return t("workorder:actionValue.yes", { defaultValue: "Yes" });
    if (raw === "false") return t("workorder:actionValue.no", { defaultValue: "No" });
  }

  // An entity id with no resolved name would be a UUID on screen. Say that it changed, not what to.
  if (fieldName && ENTITY_FIELDS.includes(fieldName)) return undefined;

  return raw;
};

const getUserLabel = (row: HistoryRow) => {
  const user = row.user;
  if (!user) return row.userId || "-";

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return user.email || fullName || user.id || "-";
};

const HistoryTab: React.FC<HistoryTabProps> = ({ workorder, refreshKey = 0 }) => {
  const { t } = useTranslation(["workorder"]);
  const [actions, setActions] = useState<WorkorderAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!workorder?.id) return;

    let cancelled = false;

    const fetchActions = async () => {
      try {
        setLoading(true);
        setFailed(false);

        const firstPage = await api.get(
          `admin/workorders/${workorder.id}/actions?page=0&size=${PAGE_SIZE}&t=${new Date().getTime()}`
        ) as WorkorderActionsPage;

        const pages = [firstPage];
        const totalPages = firstPage.totalPages || 1;

        if (totalPages > 1) {
          const remainingPages = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) =>
              api.get(
                `admin/workorders/${workorder.id}/actions?page=${index + 1}&size=${PAGE_SIZE}&t=${new Date().getTime()}`
              ) as Promise<WorkorderActionsPage>
            )
          );
          pages.push(...remainingPages);
        }

        if (!cancelled) {
          setActions(pages.flatMap((page) => page.data || []));
        }
      } catch (error) {
        console.error("Error fetching workorder history:", error);
        if (!cancelled) {
          setActions([]);
          // Distinguished from "nothing ever happened here", which is what an empty table says.
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchActions();

    return () => {
      cancelled = true;
    };
  }, [workorder?.id, refreshKey]);

  const rows = useMemo<HistoryRow[]>(() => {
    return actions
      // Odoo sync events keep their own tab, where a planner can retry them. A read-only copy here
      // would be the one they reach first and the one that cannot act.
      .filter((action) => action.source !== "EXTERNAL")
      .map((action) => {
        const field = action.fieldName;
        const from = formatValue(t, field, action.oldValue, action.oldValueLabel);
        const to = formatValue(t, field, action.newValue, action.newValueLabel);

        let change: string | undefined;
        if (from !== undefined && to !== undefined) {
          const label = formatFieldName(t, field);
          change = label ? `${label} : ${from} → ${to}` : `${from} → ${to}`;
        } else if (!OPAQUE_DESCRIPTION_ACTIONS.includes(action.actionType || "")) {
          // No old/new pair to show — fall back to the recorded description so a row that carries
          // only prose (photo added, report submitted) still says something.
          change = action.description;
        }

        return {
          id: action.id,
          created: action.created,
          actionLabel: formatActionType(t, action.actionType),
          user: action.user,
          userId: action.userId,
          change,
        };
      })
      .sort((a, b) => {
        const dateA = a.created ? new Date(a.created).getTime() : 0;
        const dateB = b.created ? new Date(b.created).getTime() : 0;
        return dateB - dateA;
      });
  }, [actions, t]);

  if (!workorder) return null;

  const tableHead = [
    {
      label: t("workorder:statusHistory.dateTime", { defaultValue: "Date & time" }),
      orderKey: "created",
    },
    {
      label: t("workorder:statusHistory.user", { defaultValue: "User" }),
      orderKey: "user",
    },
    {
      label: t("workorder:statusHistory.action", { defaultValue: "Action" }),
      orderKey: "actionType",
    },
    {
      label: t("workorder:statusHistory.change", { defaultValue: "Change" }),
      orderKey: "description",
    },
  ];

  const emptyMessage = failed
    ? t("workorder:statusHistory.failed", { defaultValue: "History could not be loaded" })
    : t("workorder:statusHistory.empty", { defaultValue: "No history" });

  return (
    <div className="p-4">
      <Table tableHead={tableHead} emptyTable={!loading && emptyMessage}>
        {loading ? (
          <tr>
            <td colSpan={tableHead.length} className="p-6 text-center">
              <div className="flex justify-center">
                <Spinner className="h-5 w-5" />
              </div>
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-300 last:border-0 hover:bg-gray-50 transition-colors">
              <td className="p-4 whitespace-nowrap">
                <Typography variant="small" color="blue-gray" className="font-normal text-xs md:text-sm">
                  {formatDateTime(row.created)}
                </Typography>
              </td>
              <td className="p-4 whitespace-nowrap md:whitespace-normal">
                <Typography variant="small" color="blue-gray" className="font-normal break-words text-xs md:text-sm">
                  {getUserLabel(row)}
                </Typography>
              </td>
              <td className="p-4 whitespace-nowrap md:whitespace-normal">
                <Typography variant="small" color="blue-gray" className="font-medium break-words text-xs md:text-sm">
                  {row.actionLabel}
                </Typography>
              </td>
              <td className="p-4 min-w-[160px] md:min-w-[260px] whitespace-normal break-words">
                <Typography variant="small" color="blue-gray" className="font-normal break-words whitespace-pre-wrap text-xs md:text-sm">
                  {row.change || "-"}
                </Typography>
              </td>
            </tr>
          ))
        )}
      </Table>
    </div>
  );
};

export default HistoryTab;
